import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, Save, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSettings = () => {
  const [settings, setSettings] = useState({
    logo_url: '',
    hero_title: '',
    hero_subtitle: '',
    stat_1_number: '',
    stat_1_label: '',
    stat_2_number: '',
    stat_2_label: '',
    stat_3_number: '',
    stat_3_label: '',
    about_text: '',
    footer_description: '',
    why_us_title: '',
    why_us_description: '',
    why_us_content: '',
    founder_message: '',
    founders: [],
    primary_color: '#C41E3A',
    secondary_color: '#8B2635',
    show_phone_numbers: true,
    show_email: true,
    show_address: true,
    show_instagram: true,
    contact_email: 'info@infinitygroup.com',
    contact_address: 'Nagpur, Maharashtra, India',
    contact_address_map_url: '',
    admin_whatsapp_number: '919511611575',
    instagram_handle: '@infinity_group.ngp',
    instagram_url: 'https://www.instagram.com/infinity_group.ngp',
    enquiry_show_email: true,
    enquiry_show_interested_project: true,
    enquiry_show_bhk_type: true,
    enquiry_show_budget: true,
    enquiry_show_source: true,
    enquiry_show_referred_by: true,
    enquiry_show_message: true,
    enquiry_custom_fields: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/settings`);
      // Ensure founders array exists
      if (!response.data.founders || response.data.founders.length === 0) {
        response.data.founders = [
          { name: 'Mr Ashay Nalamwar', title: 'Co-Founder', phone: '+919511611575', whatsapp: '919511611575' }
        ];
      }
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        setTimeout(() => {
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_user');
          window.location.href = '/admin/login';
        }, 1500);
      } else {
        toast.error('Failed to load settings');
      }
    } finally {
      setLoading(false);
    }
  };

  const addFounder = () => {
    setSettings({
      ...settings,
      founders: [...settings.founders, { name: '', title: '', qualification: '', phone: '', whatsapp: '' }]
    });
  };

  const updateFounder = (index, field, value) => {
    const newFounders = [...settings.founders];
    newFounders[index][field] = value;
    setSettings({ ...settings, founders: newFounders });
  };

  const removeFounder = (index) => {
    const newFounders = settings.founders.filter((_, i) => i !== index);
    setSettings({ ...settings, founders: newFounders });
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingLogo(true);
    const token = localStorage.getItem('admin_token');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      setSettings({ ...settings, logo_url: response.data.url });
      toast.success('Logo uploaded successfully');
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    let token = localStorage.getItem('admin_token');

    if (!token) {
      toast.error('Please login again');
      setSaving(false);
      return;
    }

    try {
      const response = await axios.put(`${API}/settings`, settings, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      });
      
      console.log('Settings saved:', response.data);
      toast.success('Settings saved successfully! Refreshing...');
      
      // Reload settings after save to confirm changes
      setTimeout(() => {
        fetchSettings();
      }, 500);
      
    } catch (error) {
      console.error('Error saving settings:', error);
      
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        setTimeout(() => {
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_user');
          window.location.href = '/admin/login';
        }, 1500);
      } else {
        const errorMessage = error.response?.data?.detail || error.message || 'Failed to save settings';
        toast.error(`Error: ${errorMessage}`);
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <nav className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <Link to="/admin/dashboard" className="flex items-center gap-3">
                <img 
                  src="https://customer-assets.emergentagent.com/job_34f43037-f49f-4896-a120-3461406a9d35/artifacts/wh0yojmq_e30d7691-c116-46a6-b645-fa81d1964b10-removebg-preview.png" 
                  alt="Infinity Group" 
                  className="h-20 w-auto"
                />
                <span className="text-xl font-bold text-gray-900">Infinity Group</span>
              </Link>
            </div>
          </div>
        </nav>
        <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/admin/dashboard" className="flex items-center gap-3">
              <img 
                src={settings.logo_url || "https://customer-assets.emergentagent.com/job_34f43037-f49f-4896-a120-3461406a9d35/artifacts/wh0yojmq_e30d7691-c116-46a6-b645-fa81d1964b10-removebg-preview.png"} 
                alt="Infinity Group" 
                className="h-20 w-auto"
              />
              <span className="text-xl font-bold text-gray-900">Infinity Group</span>
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900" data-testid="admin-settings-heading">
            Site Settings
          </h1>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="save-settings-btn"
          >
            <Save size={18} className="mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>

        <div className="space-y-8">
          {/* Logo Section */}
          <div className="bg-white rounded-xl p-6 shadow-md">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Logo</h2>
            <div className="space-y-4">
              {settings.logo_url && (
                <div className="flex items-center gap-4">
                  <img src={settings.logo_url} alt="Current Logo" className="h-24 w-auto border rounded" />
                  <p className="text-sm text-gray-600">Current Logo</p>
                </div>
              )}
              <label className="cursor-pointer inline-block">
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-500 transition-colors">
                  <Upload size={32} className="mx-auto mb-2 text-gray-400" />
                  <p className="text-sm text-gray-600">Click to upload new logo</p>
                  <p className="text-xs text-gray-400 mt-1">PNG, JPG (Max 2MB)</p>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                  disabled={uploadingLogo}
                />
              </label>
              {uploadingLogo && <p className="text-sm text-blue-600">Uploading logo...</p>}
            </div>
          </div>

          {/* Hero Section */}
          <div className="bg-white rounded-xl p-6 shadow-md">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Hero Section</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Hero Title</label>
                <input
                  type="text"
                  value={settings.hero_title}
                  onChange={(e) => setSettings({ ...settings, hero_title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="Quality homes, built to last."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Hero Subtitle</label>
                <textarea
                  value={settings.hero_subtitle}
                  onChange={(e) => setSettings({ ...settings, hero_subtitle: e.target.value })}
                  rows="2"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="Discover premium residential apartments..."
                ></textarea>
              </div>
            </div>
          </div>

          {/* Stats Section */}
          <div className="bg-white rounded-xl p-6 shadow-md">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Statistics</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Stat 1 Number</label>
                <input
                  type="text"
                  value={settings.stat_1_number}
                  onChange={(e) => setSettings({ ...settings, stat_1_number: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="15+"
                />
                <label className="block text-sm font-medium text-gray-700 mt-2 mb-2">Stat 1 Label</label>
                <input
                  type="text"
                  value={settings.stat_1_label}
                  onChange={(e) => setSettings({ ...settings, stat_1_label: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="Years of Experience"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Stat 2 Number</label>
                <input
                  type="text"
                  value={settings.stat_2_number}
                  onChange={(e) => setSettings({ ...settings, stat_2_number: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="25+"
                />
                <label className="block text-sm font-medium text-gray-700 mt-2 mb-2">Stat 2 Label</label>
                <input
                  type="text"
                  value={settings.stat_2_label}
                  onChange={(e) => setSettings({ ...settings, stat_2_label: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="Projects Delivered"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Stat 3 Number</label>
                <input
                  type="text"
                  value={settings.stat_3_number}
                  onChange={(e) => setSettings({ ...settings, stat_3_number: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="500+"
                />
                <label className="block text-sm font-medium text-gray-700 mt-2 mb-2">Stat 3 Label</label>
                <input
                  type="text"
                  value={settings.stat_3_label}
                  onChange={(e) => setSettings({ ...settings, stat_3_label: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="Happy Families"
                />
              </div>
            </div>
          </div>

          {/* About Section */}
          <div className="bg-white rounded-xl p-6 shadow-md">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">About Section</h2>
            <textarea
              value={settings.about_text}
              onChange={(e) => setSettings({ ...settings, about_text: e.target.value })}
              rows="3"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              placeholder="Infinity Group - We don't just build homes..."
            ></textarea>
          </div>

          {/* Footer Section */}
          <div className="bg-white rounded-xl p-6 shadow-md">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Footer</h2>
            <textarea
              value={settings.footer_description}
              onChange={(e) => setSettings({ ...settings, footer_description: e.target.value })}
              rows="2"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              placeholder="Quality homes, built to last..."
            ></textarea>
          </div>

          {/* Founders / Contact Section */}
          <div className="bg-white rounded-xl p-6 shadow-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Founder Contacts</h2>
              <button
                type="button"
                onClick={addFounder}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
              >
                + Add Founder
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">Add up to 3 founders. UI will adjust automatically.</p>
            <div className="space-y-4">
              {settings.founders && settings.founders.map((founder, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4 relative">
                  <button
                    type="button"
                    onClick={() => removeFounder(index)}
                    className="absolute top-2 right-2 text-red-600 hover:text-red-800"
                  >
                    <X size={20} />
                  </button>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pr-8">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                      <input
                        type="text"
                        value={founder.name}
                        onChange={(e) => updateFounder(index, 'name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="Mr. John Doe"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                      <input
                        type="text"
                        value={founder.title}
                        onChange={(e) => updateFounder(index, 'title', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="Co-Founder"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Qualification / Education <span className="text-gray-400 font-normal">(optional)</span>
                      </label>
                      <input
                        type="text"
                        value={founder.qualification || ''}
                        onChange={(e) => updateFounder(index, 'qualification', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="e.g., B.E. Civil Engineering, IIT Bombay"
                        data-testid={`input-founder-qualification-${index}`}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                      <input
                        type="text"
                        value={founder.phone}
                        onChange={(e) => updateFounder(index, 'phone', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="+91 98765 43210"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp Number (with country code, no +)</label>
                      <input
                        type="text"
                        value={founder.whatsapp}
                        onChange={(e) => updateFounder(index, 'whatsapp', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="919876543210"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Why Us Section */}
          <div className="bg-white rounded-xl p-6 shadow-md">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Why Us Page Content</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Page Title</label>
                <input
                  type="text"
                  value={settings.why_us_title}
                  onChange={(e) => setSettings({ ...settings, why_us_title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="Why Choose Infinity Group?"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle</label>
                <textarea
                  value={settings.why_us_description}
                  onChange={(e) => setSettings({ ...settings, why_us_description: e.target.value })}
                  rows="2"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="With over a decade of experience..."
                ></textarea>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Main Content</label>
                <textarea
                  value={settings.why_us_content}
                  onChange={(e) => setSettings({ ...settings, why_us_content: e.target.value })}
                  rows="4"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="Founded in 2010, Infinity Group..."
                ></textarea>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Founder Message</label>
                <textarea
                  value={settings.founder_message}
                  onChange={(e) => setSettings({ ...settings, founder_message: e.target.value })}
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="At Infinity Group, we don't just build apartments..."
                ></textarea>
              </div>
            </div>
          </div>

          {/* Color Theme */}
          <div className="bg-white rounded-xl p-6 shadow-md">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Color Theme</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Primary Color</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={settings.primary_color}
                    onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                    className="h-12 w-20 border border-gray-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={settings.primary_color}
                    onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                    placeholder="#C41E3A"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Secondary Color</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={settings.secondary_color}
                    onChange={(e) => setSettings({ ...settings, secondary_color: e.target.value })}
                    className="h-12 w-20 border border-gray-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={settings.secondary_color}
                    onChange={(e) => setSettings({ ...settings, secondary_color: e.target.value })}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                    placeholder="#8B2635"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Contact Info & Privacy Settings */}
          <div className="bg-white rounded-xl p-6 shadow-md">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Contact Info & Privacy</h2>
            <p className="text-sm text-gray-600 mb-6">Control what contact information is visible on the website footer.</p>
            
            {/* Visibility Toggles */}
            <div className="space-y-4 mb-6">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">Show Phone Numbers</p>
                  <p className="text-sm text-gray-500">Display founder phone numbers in footer</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.show_phone_numbers}
                    onChange={(e) => setSettings({ ...settings, show_phone_numbers: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">Show Email</p>
                  <p className="text-sm text-gray-500">Display contact email in footer</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.show_email}
                    onChange={(e) => setSettings({ ...settings, show_email: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">Show Address</p>
                  <p className="text-sm text-gray-500">Display office address in footer</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.show_address}
                    onChange={(e) => setSettings({ ...settings, show_address: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">Show Instagram</p>
                  <p className="text-sm text-gray-500">Display Instagram handle in footer</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.show_instagram}
                    onChange={(e) => setSettings({ ...settings, show_instagram: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                </label>
              </div>
            </div>

            {/* Editable Contact Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Contact Email</label>
                <input
                  type="email"
                  value={settings.contact_email}
                  onChange={(e) => setSettings({ ...settings, contact_email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="info@infinitygroup.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                <input
                  type="text"
                  value={settings.contact_address}
                  onChange={(e) => setSettings({ ...settings, contact_address: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="Nagpur, Maharashtra, India"
                  data-testid="input-contact-address"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Google Maps URL <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  type="url"
                  value={settings.contact_address_map_url || ''}
                  onChange={(e) => setSettings({ ...settings, contact_address_map_url: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="https://maps.app.goo.gl/... or https://www.google.com/maps/place/..."
                  data-testid="input-contact-address-map-url"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Open Google Maps, search your office, click <strong>Share</strong> → <strong>Copy link</strong>, and paste here.
                  If left empty, clicking the address will search Google Maps using the address text above.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Default Admin WhatsApp Number
                </label>
                <input
                  type="text"
                  value={settings.admin_whatsapp_number || ''}
                  onChange={(e) => setSettings({ ...settings, admin_whatsapp_number: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="919511611575 (with country code, no + or spaces)"
                  data-testid="input-admin-whatsapp"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Used for the "Enquire Now" button across the site and as a fallback when sharing feedback links.
                  Format: country code + number, no + sign or spaces (e.g., <code>919511611575</code>).
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Instagram Handle</label>
                <input
                  type="text"
                  value={settings.instagram_handle}
                  onChange={(e) => setSettings({ ...settings, instagram_handle: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="@infinity_group.ngp"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Instagram URL</label>
                <input
                  type="text"
                  value={settings.instagram_url}
                  onChange={(e) => setSettings({ ...settings, instagram_url: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="https://www.instagram.com/infinity_group.ngp"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Enquiry Form Fields */}
        <div className="mt-8 bg-white rounded-xl p-6 shadow-md">
          <h2 className="text-xl font-bold text-gray-900 mb-2">Enquiry Form Fields</h2>
          <p className="text-sm text-gray-500 mb-6">
            Toggle fields on or off, and add custom fields. <strong>Name & Phone</strong> are always shown (required for follow-up).
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {[
              { key: 'enquiry_show_email', label: 'Email Address' },
              { key: 'enquiry_show_interested_project', label: 'Interested Property' },
              { key: 'enquiry_show_bhk_type', label: 'BHK Type' },
              { key: 'enquiry_show_budget', label: 'Budget Range' },
              { key: 'enquiry_show_source', label: 'How did you hear about us?' },
              { key: 'enquiry_show_referred_by', label: 'Referred By' },
              { key: 'enquiry_show_message', label: 'Additional Message' },
            ].map((f) => (
              <div key={f.key} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <p className="font-medium text-gray-800">{f.label}</p>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings[f.key] !== false}
                    onChange={(e) => setSettings({ ...settings, [f.key]: e.target.checked })}
                    className="sr-only peer"
                    data-testid={`toggle-${f.key}`}
                  />
                  <div className="w-11 h-6 bg-gray-300 rounded-full peer peer-checked:bg-blue-600 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                </label>
              </div>
            ))}
          </div>

          <div className="border-t pt-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Custom Fields</h3>
                <p className="text-sm text-gray-500">Add extra questions specific to your business.</p>
              </div>
              <Button
                type="button"
                onClick={() => setSettings({
                  ...settings,
                  enquiry_custom_fields: [...(settings.enquiry_custom_fields || []), { label: '', placeholder: '', type: 'text', required: false }],
                })}
                className="bg-gray-100 text-gray-800 hover:bg-gray-200"
                data-testid="add-custom-field-btn"
              >
                + Add Custom Field
              </Button>
            </div>

            {(settings.enquiry_custom_fields || []).length === 0 && (
              <p className="text-sm text-gray-400 italic">No custom fields yet. Click "Add Custom Field" to create one.</p>
            )}

            <div className="space-y-3">
              {(settings.enquiry_custom_fields || []).map((field, idx) => (
                <div key={idx} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start p-4 border border-gray-200 rounded-lg" data-testid={`custom-field-row-${idx}`}>
                  <div className="md:col-span-4">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Field Label *</label>
                    <input
                      type="text"
                      value={field.label || ''}
                      onChange={(e) => {
                        const arr = [...settings.enquiry_custom_fields];
                        arr[idx] = { ...arr[idx], label: e.target.value };
                        setSettings({ ...settings, enquiry_custom_fields: arr });
                      }}
                      placeholder="e.g., Preferred move-in date"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  <div className="md:col-span-4">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Placeholder</label>
                    <input
                      type="text"
                      value={field.placeholder || ''}
                      onChange={(e) => {
                        const arr = [...settings.enquiry_custom_fields];
                        arr[idx] = { ...arr[idx], placeholder: e.target.value };
                        setSettings({ ...settings, enquiry_custom_fields: arr });
                      }}
                      placeholder="Hint shown inside the field"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                    <select
                      value={field.type || 'text'}
                      onChange={(e) => {
                        const arr = [...settings.enquiry_custom_fields];
                        arr[idx] = { ...arr[idx], type: e.target.value };
                        setSettings({ ...settings, enquiry_custom_fields: arr });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    >
                      <option value="text">Text</option>
                      <option value="textarea">Textarea</option>
                    </select>
                  </div>
                  <div className="md:col-span-1 flex items-center justify-center pt-6">
                    <label className="inline-flex items-center gap-1 text-xs text-gray-600">
                      <input
                        type="checkbox"
                        checked={field.required || false}
                        onChange={(e) => {
                          const arr = [...settings.enquiry_custom_fields];
                          arr[idx] = { ...arr[idx], required: e.target.checked };
                          setSettings({ ...settings, enquiry_custom_fields: arr });
                        }}
                        className="w-4 h-4"
                      />
                      Required
                    </label>
                  </div>
                  <div className="md:col-span-1 flex items-start pt-6">
                    <button
                      type="button"
                      onClick={() => {
                        const arr = settings.enquiry_custom_fields.filter((_, i) => i !== idx);
                        setSettings({ ...settings, enquiry_custom_fields: arr });
                      }}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 rounded p-2"
                      title="Remove field"
                      data-testid={`remove-custom-field-${idx}`}
                    >
                      <X size={18} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-8 flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 px-8"
            data-testid="save-settings-bottom-btn"
          >
            <Save size={18} className="mr-2" />
            {saving ? 'Saving...' : 'Save All Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
